/**
 * applyUiActions — UI-action directive processor (PR #3277). A plain function
 * taking an explicit deps object so ChatBox's `applyUiActions` useCallback shell
 * keeps the same dependency array. Dedup semantics, report.init cross-path dedup,
 * module.focus sequence tracking, pages.open replay, and re-entrant
 * `applyUiActionsRef.current(...)` calls must all be preserved exactly.
 *
 * `api` and `queryKeys` are static module imports; only runtime values/refs are
 * passed in deps.
 */
import type { MutableRefObject } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { api } from '@/lib/api';
import { createEmailTemplate } from '@/lib/api/endpoints/email-templates';
import { queryKeys } from '@/lib/query/keys';
import { buildConversationPath } from '@/lib/workspace-routing';
import { prefetchModuleChunk } from '@/lib/prefetch-module';
import type { QueryClient } from '@tanstack/react-query';
import type { ChatUIAction } from '@/lib/api/endpoints/chat';
import type { WorkspaceModule } from '@/lib/api/endpoints/workspaces';
import type { Module } from '@/types/modules';

type FocusModule = (
  moduleId: string,
  options?: { anchorMessageId?: string | null; shouldScrollIntoView?: boolean },
) => void;

/**
 * Build the optional `meta` payload for a `module.focus` → `selectObject` call.
 *  - `doc`     → `forceEditor` (open the Xavior doc editor, never the Drive browser)
 *  - `boardId` → kanban board scoping
 *  - `dateISO` → a recurring event's clicked OCCURRENCE date, so the calendar
 *                lands on the right day instead of the master's first occurrence
 * Returns `undefined` when there is nothing to attach (preserves prior behavior).
 */
function buildSelectionMeta(action: ChatUIAction): Record<string, unknown> | undefined {
  const meta: Record<string, unknown> = {};
  if ((action as any).objectType === 'doc') meta.forceEditor = true;
  if ((action as any).boardId) meta.boardId = (action as any).boardId;
  if (typeof (action as any).dateISO === 'string' && (action as any).dateISO) {
    meta.dateISO = (action as any).dateISO;
  }
  return Object.keys(meta).length > 0 ? meta : undefined;
}

export interface ApplyUiActionsDeps {
  activeConversation: { id?: string } | null | undefined;
  availableModules: Module[];
  token: string | null;
  workspaceId: string;
  queryClient: QueryClient;
  navigate: NavigateFunction;
  selectObject: (type: any, id: string, meta?: Record<string, unknown>) => void;
  showToast: (
    message: string,
    type: 'success' | 'error' | 'info',
    errorContext?: { message: string; context?: string },
    action?: { label: string; onClick: () => void },
  ) => void;
  /** Programmatic chat-pane open — jumps the chat dock straight to 'open' (ChatDockContext). */
  expandChatPane: () => void;
  deleteConversation: (conversationId: string) => Promise<void>;
  refreshWorkspaceModuleCatalog: () => Promise<WorkspaceModule[]>;
  ensureModulePanelInConversationOnce: (conversationId: string, moduleId: string) => Promise<boolean>;
  refreshPanelBoundModules: (
    conversationId: string,
    options?: { forceRefresh?: boolean },
  ) => Promise<Module[]>;
  invalidateConversationStructure: (conversationId: string) => void;
  queuePendingModuleFocus: (focus: {
    conversationId: string;
    moduleId: string;
    anchorMessageId: string | null;
    sequence: number;
  }) => void;
  workspaceModuleCatalogRef: MutableRefObject<WorkspaceModule[]>;
  moduleFocusSequenceRef: MutableRefObject<number>;
  lastAppliedModuleFocusSequenceRef: MutableRefObject<number>;
  activeConversationIdRef: MutableRefObject<string | null>;
  applyUiActionsRef: MutableRefObject<(actions: ChatUIAction[]) => void>;
  focusModuleRef: MutableRefObject<FocusModule>;
}

export function runApplyUiActions(actions: ChatUIAction[], deps: ApplyUiActionsDeps): void {
  const {
    activeConversation,
    availableModules,
    token,
    workspaceId,
    queryClient,
    navigate,
    selectObject,
    showToast,
    expandChatPane,
    deleteConversation,
    refreshWorkspaceModuleCatalog,
    ensureModulePanelInConversationOnce,
    refreshPanelBoundModules,
    invalidateConversationStructure,
    queuePendingModuleFocus,
    workspaceModuleCatalogRef,
    moduleFocusSequenceRef,
    lastAppliedModuleFocusSequenceRef,
    activeConversationIdRef,
    applyUiActionsRef,
    focusModuleRef,
  } = deps;
    console.log('[ChatBox] applyUiActions called:', actions.length, 'actions', actions.map(a => a?.type));
    if (!activeConversation?.id || actions.length === 0) return;
    const dedupedByKey = new Map<string, ChatUIAction>();
    const activeConversationId = activeConversation.id;

    for (const action of actions) {
      if (!action || typeof action.type !== 'string') continue;
      if (action.conversationId && action.conversationId !== activeConversationId) continue;

      // module.focus deduplicates across all modules — only the last one in a batch applies.
      // This prevents multiple modules stacking when several focus actions fire in a batch.
      const dedupeKey =
        action.idempotencyKey
        || (action.type === 'module.focus' ? 'module.focus'
          : `${action.type}:${action.moduleId || (action as any).section?.id || action.href || action.message || ''}`);
      // Keep the last occurrence while preserving chronological execution order.
      if (dedupedByKey.has(dedupeKey)) {
        dedupedByKey.delete(dedupeKey);
      }
      dedupedByKey.set(dedupeKey, action);
    }

    const dedupedActions = Array.from(dedupedByKey.values());
    for (const action of dedupedActions) {
      if (!action || typeof action.type !== 'string') continue;

      // demo.cleared — the clear_demo_data agent tool purged the demo dataset
      // server-side. Refresh the caches exactly like the navbar "Remove demo
      // data" button does (RemoveDemoDataButton) so the UI drops the demo
      // objects immediately and the demo-seed status flips to not-loaded (which
      // hides the navbar pill + end-of-demo chip).
      if (action.type === 'demo.cleared') {
        queryClient.invalidateQueries({
          predicate: (q) => {
            const k = JSON.stringify(q.queryKey);
            return (
              k.includes('email') || k.includes('gmail') ||
              k.includes('integration') || k.includes('object') ||
              k.includes('module') || k.includes('demo-seed')
            );
          },
        });
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('xavior:demo-data-loaded'));
        }
        continue;
      }

      // Report directives (report.init, report.section, report.status)
      // On streaming (web), these are handled mid-stream in the SSE loop.
      // On non-streaming (Capacitor/native), they arrive here after the full response.
      if (action.type === 'report.init') {
        void (async () => {
          const reportTheme = (typeof action.theme === 'string' ? action.theme : 'formal') as any
          const reportTitle = typeof action.title === 'string' ? action.title
            : typeof action.label === 'string' ? action.label : 'Report'
          const initialConfig = {
            theme: reportTheme,
            status: 'building' as const,
            statusMessage: 'Initializing report...',
            sections: [] as any[],
          }
          // Cross-path dedup (#1818): useChat.ts ALSO has a report.init
          // handler for the streaming/web path. When the agent's report
          // builder emits a duplicate report.init, both handlers can fire
          // and each creates a fresh module. Look up the workspace modules
          // cache for an in-flight or recently-created report with the same
          // normalized title and skip the create if found. The cache is
          // shared across both paths via React Query, so this dedupes
          // against whichever code path got there first.
          const titleNorm = reportTitle.trim().toLowerCase()
          const cachedModulesForDedup = queryClient.getQueryData<any[]>(queryKeys.modules.list(workspaceId))
          if (Array.isArray(cachedModulesForDedup)) {
            const dup = cachedModulesForDedup.find(
              (m: any) =>
                m?.type === 'report'
                && !m?.archived
                && !m?.archivedAt
                && typeof m?.label === 'string'
                && m.label.trim().toLowerCase() === titleNorm,
            )
            if (dup) {
              console.info('[ChatBox] report.init: duplicate of existing report module, ignoring', { dupId: dup.id, reportTitle })
              return
            }
          }
          // Look up the workspace's Pages module so the report is parented
          // under it (mirrors the html.init flow). Server falls back to
          // auto-parent if we don't pass it.
          let reportParentId: string | undefined
          try {
            const cachedModules = queryClient.getQueryData<any[]>(queryKeys.modules.list(workspaceId))
            if (Array.isArray(cachedModules)) {
              const pagesModule = cachedModules.find(
                (m: any) => m?.type === 'pages' && !m?.archived && !m?.archivedAt,
              )
              if (pagesModule?.id) reportParentId = pagesModule.id
            }
          } catch (lookupErr) {
            console.warn('[ChatBox] Pages module lookup failed for report:', lookupErr)
          }
          try {
            const createdModule = await api.workspaces.createWorkspaceModule(token, workspaceId, {
              type: 'report',
              label: reportTitle,
              position: -1000, // Place at top of sidebar
              ...(reportParentId ? { parentId: reportParentId } : {}),
              config: initialConfig as unknown as Record<string, unknown>,
            })
            // Process any queued report.section/report.status actions for this report
            const reportActions = dedupedActions.filter(a =>
              a.type === 'report.section' || a.type === 'report.status'
            )
            const config = { ...initialConfig }
            for (const ra of reportActions) {
              if (ra.type === 'report.section' && ra.section) {
                const sectionData = ra.section as any
                if (typeof sectionData.id === 'string') {
                  const existingIdx = config.sections.findIndex((s: any) => s.id === sectionData.id)
                  const section = { ...sectionData, visible: true }
                  if (existingIdx >= 0) {
                    config.sections[existingIdx] = section
                  } else {
                    config.sections.push(section)
                  }
                }
              } else if (ra.type === 'report.status') {
                config.statusMessage = typeof ra.message === 'string' ? ra.message : config.statusMessage
                if (ra.status === 'complete') (config as any).status = 'complete'
              }
            }
            // Save final config (sections + status updates)
            await api.workspaces.updateWorkspaceModule(token, workspaceId, createdModule.id, {
              config: config as unknown as Record<string, unknown>,
            })
            // Refresh modules and focus the report
            await refreshWorkspaceModuleCatalog()
            focusModuleRef.current(createdModule.id, {})
          } catch (err) {
            console.error('[ChatBox] Failed to create report module (native):', err)
          }
        })()
        // Skip report.section/report.status since they're processed above with report.init
        continue
      }
      if (action.type === 'report.section' || action.type === 'report.status') {
        // These are handled inside report.init above when batched together.
        // If they arrive without a report.init (e.g., updating an existing report),
        // they would need separate handling. For now, skip.
        continue
      }

      // `onboarding.advance` / `onboarding.complete` directives are no longer
      // honored: the user-level 5-state machine they drove was retired (#4420).
      // Chat-window onboarding is driven by useChatOnboarding (recipe session +
      // onboarding_completed latch), not by a streamed directive. Silently drop
      // any stale directive so an old conversation can't POST to a dead route.
      if (action.type === 'onboarding.advance' || action.type === 'onboarding.complete') {
        continue;
      }

      // Agent renamed this chat on the user's request. The API PATCH already
      // persisted it (titleSource='user', locked); refresh so the sidebar/header
      // show the new name immediately instead of waiting for a natural refetch.
      if (action.type === 'conversation.rename') {
        const targetId = action.conversationId || activeConversationId;
        if (targetId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.conversations.lists(),
            refetchType: 'all',
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.conversations.detail(targetId),
            refetchType: 'all',
          });
        }
        continue;
      }

      // Onboarding: archive/delete conversation
      if (action.type === 'conversation.archive') {
        const targetId = (action as any).conversationId || activeConversationId;
        if (targetId) {
          void deleteConversation(targetId).catch((err: unknown) => {
            console.error('[ChatBox] Failed to archive conversation:', err);
          });
        }
        continue;
      }

      // email.create — the xavior-email skill emits this after drafting an
      // email, so the generated HTML gets persisted as a reusable Studio
      // template via the Phase-1 email-templates endpoint (rather than living
      // only in chat history). Fire-and-forget like conversation.archive
      // above: don't block the action loop on the network round-trip.
      if (action.type === 'email.create') {
        const name = typeof action.name === 'string' ? action.name.trim() : '';
        const subject = typeof action.subject === 'string' ? action.subject : '';
        const html = typeof action.html === 'string' ? action.html : '';
        const grade = action.grade === 'campaign' || action.grade === 'correspondence' ? action.grade : null;
        if (!name || !subject || !html || !grade) {
          console.warn('[ChatBox] email.create: malformed action, skipping', action);
          continue;
        }
        void createEmailTemplate(workspaceId, {
          name,
          subject,
          html,
          text: '',
          grade,
          source: { kind: 'studio' },
        }).then((created) => {
          queryClient.invalidateQueries({ queryKey: queryKeys.emailTemplates.list(workspaceId) });
          // #5539: give the user a one-click path from chat to the email instead
          // of hunting for it in Studio. The CTA opens the Studio (pages) module,
          // switches to the Templates tab, and previews THIS template. Falls back
          // to a plain toast if the id or Studio module isn't available.
          const templateId = created?.id;
          const conversationId = activeConversation?.id ?? activeConversationIdRef.current ?? undefined;
          const pagesModuleId = workspaceModuleCatalogRef.current.find(
            (m) => !m.archivedAt && m.type === 'pages',
          )?.id;
          if (templateId && conversationId && pagesModuleId) {
            const openInStudio = () => {
              // Persist the intent BEFORE navigating so Studio applies it on mount
              // even when it isn't listening yet; the live event covers the
              // already-mounted case. Distinct key from the new-template intent so
              // the two consumers never race on the same sessionStorage slot.
              try {
                window.sessionStorage.setItem(
                  `xavior:studio-open-template:${pagesModuleId}`,
                  JSON.stringify({ templateId, ts: Date.now() }),
                );
              } catch { /* private mode — the live event still covers mounted Studio */ }
              navigate(buildConversationPath(workspaceId, conversationId, { moduleId: pagesModuleId }));
              window.dispatchEvent(new CustomEvent('xavior:force-module-focus', { detail: { moduleId: pagesModuleId } }));
              window.dispatchEvent(new CustomEvent('xavior:studio-open-template', { detail: { moduleId: pagesModuleId, templateId } }));
            };
            showToast(`Saved "${name}" to Studio Templates`, 'success', undefined, {
              label: 'Open in Studio',
              onClick: openInStudio,
            });
          } else {
            showToast(`Saved "${name}" to Studio Templates`, 'success');
          }
        }).catch((err: unknown) => {
          console.error('[ChatBox] Failed to save email.create template:', err);
          showToast("Couldn't save email template", 'error');
        });
        continue;
      }

      // Resolve moduleType to moduleId if needed (e.g., agent emits moduleType: "email")
      if (action.type === 'module.focus' && !action.moduleId && action.moduleType) {
        const moduleType = action.moduleType;
        // Warm the module's lazy chunk while the catalog lookup/refresh runs —
        // an agent-focused module renders with no hover opportunity (no-op for
        // bundled types, deduped per chunk).
        prefetchModuleChunk(moduleType);
        const match = workspaceModuleCatalogRef.current.find(m => m.type === moduleType);
        if (match) {
          action.moduleId = match.id;
        } else {
          // Catalog may not be loaded yet — refresh and retry asynchronously
          void (async () => {
            const refreshed = await refreshWorkspaceModuleCatalog();
            const retryMatch = refreshed.find(m => m.type === moduleType);
            if (retryMatch) {
              applyUiActionsRef.current([{ ...action, moduleId: retryMatch.id }]);
            } else {
              console.warn('[ChatBox] module.focus: no module found for moduleType', moduleType);
              showToast("Couldn't open that module", 'error');
            }
          })();
          continue;
        }
      }

      // pages.open — focus the Pages container AND auto-select a specific
      // html_viewer child. Used by the xavior-letter skill after generating
      // the letter, so the user lands inside the Pages chrome with the new
      // letter open instead of having to navigate manually. Mirrors the
      // pages-pending-child pattern from useChat.ts:2713-2736 (html.init).
      if (action.type === 'pages.open'
          && typeof (action as any).parentPagesId === 'string'
          && typeof (action as any).childModuleId === 'string') {
        const parentPagesId = (action as any).parentPagesId as string;
        const childModuleId = (action as any).childModuleId as string;
        // Stash the pending child request BEFORE we trigger the focus.
        // PagesContainerModule reads this on mount — the dispatch alone
        // races with React's mount cycle (focus → mount →
        // registerListener) and the event can land before the listener
        // exists.
        try {
          window.sessionStorage.setItem(
            `xavior:pages-pending-child:${parentPagesId}`,
            JSON.stringify({ childModuleId, childSource: 'module', ts: Date.now() }),
          );
        } catch { /* sessionStorage unavailable */ }
        // Keep the chat pane visible alongside the Pages module so the
        // user sees both their letter AND the agent's follow-up prose +
        // chips (e.g., "Want a daily briefing?"). Without this, a user
        // who previously toggled the chevron collapse via localStorage
        // would land in a Pages-only view with the chat hidden.
        expandChatPane();
        // Replay through the same applyUiActions path so module.focus's
        // existing logic (catalog refresh, sequence tracking, conversation
        // panel insert, etc.) runs.
        applyUiActionsRef.current([{
          type: 'module.focus',
          moduleId: parentPagesId,
          skipInvalidation: true,
        } as any]);
        // Live event in case the Pages container is already mounted —
        // its listener picks this up and switches children without a
        // remount round-trip.
        window.dispatchEvent(new CustomEvent('xavior:pages-open-child', {
          detail: { parentPagesId, childModuleId, childSource: 'module' },
        }));
        continue;
      }

      if (action.type === 'module.focus' && typeof action.moduleId === 'string') {
        // Warm the target module's lazy chunk before the attach/refresh
        // round-trips below, so an agent-opened module mounts from the module
        // cache instead of cold-fetching its chunk (no-op for bundled types).
        const focusedModuleType = availableModules.find((m) => m.id === action.moduleId)?.type
          ?? workspaceModuleCatalogRef.current.find((m) => m.id === action.moduleId)?.type;
        if (focusedModuleType) prefetchModuleChunk(focusedModuleType);
        // Invalidate object queries for this module so freshly-linked items appear
        queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('asset') });
        // Invalidate stat queries for health module updates
        queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('stat') });
        queryClient.invalidateQueries({ queryKey: ['health-profile'] });
        // If the action has a healthDate, dispatch a custom event so the health module navigates to it
        if (typeof (action as any).healthDate === 'string') {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('health-navigate-date', { detail: { date: (action as any).healthDate } }));
          }, 500);
        }
        console.log('[ChatBox] module.focus action received:', {
          moduleId: action.moduleId,
          objectId: action.objectId,
          conversationId: action.conversationId,
          activeConversationId,
          moduleExistsInActiveConversation: availableModules.some(m => m.id === action.moduleId),
          availableModuleIds: availableModules.map(m => `${m.id} (${m.label || m.type})`),
        });
        if (action.conversationId && action.conversationId !== activeConversationId) continue;
        const sequence = ++moduleFocusSequenceRef.current;
        const anchorMessageId = typeof action.anchorMessageId === 'string'
          ? action.anchorMessageId
          : null;
        const moduleExistsInActiveConversation = availableModules.some(
          (module) => module.id === action.moduleId,
        );
        // If a Drive sub-module is focused but a parent Drive panel already exists,
        // redirect focus to the parent Drive module instead of creating a new panel
        let resolvedModuleId = action.moduleId!;
        if (!moduleExistsInActiveConversation) {
          const parentDriveModule = availableModules.find(
            (m) => m.type === 'drive' && (m.config as any)?.isRoot === true,
          );
          if (parentDriveModule) {
            resolvedModuleId = parentDriveModule.id;
          }
        }
        // skipInvalidation: the caller (e.g. report.init) wants to open the panel
        // without the heavy query invalidation cascade that can abort an active
        // SSE stream. We still attach the panel and focus it, but defer invalidation
        // to a later module.focus call (e.g. at stream completion).
        const skipInvalidation = !!(action as any).skipInvalidation;
        const moduleStillMissing = !availableModules.some((m) => m.id === resolvedModuleId);
        if (moduleStillMissing) {
          void (async () => {
            const attached = await ensureModulePanelInConversationOnce(activeConversationId, resolvedModuleId);
            if (!attached) {
              console.warn('[ChatBox] Unable to attach module for module.focus action', {
                conversationId: activeConversationId,
                moduleId: resolvedModuleId,
              });
              showToast("Couldn't open that module", 'error');
              return;
            }
            if (activeConversationIdRef.current !== activeConversationId) return;
            // The attach upserts the new panel into the local cache, so resolve
            // from cache first (instant, no round-trip). If it still misses — the
            // workspace-module catalog entry isn't loaded yet, or the panels list
            // endpoint is lagging the create (read-after-write) — fall back to a
            // bounded force-refresh retry. Without this, a single stale read drops
            // the focus into the pending queue and strands the user on the module
            // they were just in (the "module won't open" sidebar bug).
            let panelModules = await refreshPanelBoundModules(activeConversationId);
            if (activeConversationIdRef.current !== activeConversationId) return;
            let resolvedFromPanels = panelModules.some((module) => module.id === resolvedModuleId);
            for (let attempt = 0; !resolvedFromPanels && attempt < 4; attempt += 1) {
              await new Promise((resolve) => setTimeout(resolve, 150));
              panelModules = await refreshPanelBoundModules(activeConversationId, { forceRefresh: true });
              if (activeConversationIdRef.current !== activeConversationId) return;
              resolvedFromPanels = panelModules.some((module) => module.id === resolvedModuleId);
            }
            if (resolvedFromPanels) {
              if (!skipInvalidation) {
                invalidateConversationStructure(activeConversationId);
                // Refresh sidebar so newly-created modules appear
                queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
              }
              if (sequence <= lastAppliedModuleFocusSequenceRef.current) return;
              lastAppliedModuleFocusSequenceRef.current = sequence;
              focusModuleRef.current(resolvedModuleId, { anchorMessageId });
              // Dispatch objectId selection for async path too
              if (typeof action.objectId === 'string' && action.objectId) {
                const focusedModule = panelModules.find(m => m.id === resolvedModuleId);
                const objectTypeFallbacks: Record<string, string> = {
                  task_list: 'task', kanban: 'task', contact_list: 'contact', client_list: 'contact', crm: 'contact',
                  people_list: 'contact', note_grid: 'note', event_list: 'event',
                  calendar: 'event', timeline: 'event', place_list: 'place', place_card: 'place',
                  place_detail: 'place', drive: 'asset', file_viewer: 'asset', email: 'email',
                };
                // An explicit objectType on the action wins over the
                // moduleType→objectType fallback map (see sync path below).
                const objType = (typeof (action as any).objectType === 'string' && (action as any).objectType)
                  || focusedModule?.objectType
                  || (focusedModule?.type ? objectTypeFallbacks[focusedModule.type] : undefined);
                if (objType) {
                  const asyncMeta = buildSelectionMeta(action);
                  setTimeout(() => selectObject(objType as any, action.objectId as string, asyncMeta), 800);
                }
              }
              return;
            }
            if (sequence <= lastAppliedModuleFocusSequenceRef.current) return;
            queuePendingModuleFocus({
              conversationId: activeConversationId,
              moduleId: action.moduleId!,
              anchorMessageId,
              sequence,
            });
            if (!skipInvalidation) {
              invalidateConversationStructure(activeConversationId);
              queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
            }
          })();
          continue;
        }
        // Invalidate sidebar module list whenever a module is focused (it may be newly created)
        if (!skipInvalidation) {
          queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
        }
        lastAppliedModuleFocusSequenceRef.current = sequence;
        focusModuleRef.current(action.moduleId, { anchorMessageId });

        // If the action includes an objectId, dispatch object selection so the module opens that item
        if (typeof action.objectId === 'string' && action.objectId) {
          const focusedModule = availableModules.find(m => m.id === action.moduleId);
          const objectTypeFallbacks: Record<string, string> = {
            task_list: 'task', kanban: 'task', contact_list: 'contact', client_list: 'contact', crm: 'contact',
            people_list: 'contact', note_grid: 'note', event_list: 'event',
            calendar: 'event', timeline: 'event', place_list: 'place', place_card: 'place',
            place_detail: 'place', drive: 'asset', file_viewer: 'asset', email: 'email',
          };
          // An explicit objectType on the action wins over the moduleType→objectType
          // fallback map. Without this, note opens resolve 'drive'→'asset' and never
          // fire DriveModule's note listener.
          const objType = (typeof (action as any).objectType === 'string' && (action as any).objectType)
            || focusedModule?.objectType
            || (focusedModule?.type ? objectTypeFallbacks[focusedModule.type] : undefined);
          if (objType) {
            // Delay to let the module mount and load data
            const objectId = action.objectId as string;
            const meta = buildSelectionMeta(action);
            setTimeout(() => selectObject(objType as any, objectId, meta), 500);
          }
        }
        continue;
      }

      if (action.type === 'navigate' && typeof action.href === 'string') {
        const href = action.href.trim();
        if (!href || /^https?:\/\//i.test(href)) continue;
        navigate(href, { replace: Boolean(action.replace) });
        continue;
      }

      if (action.type === 'toast' && typeof action.message === 'string' && action.message.trim()) {
        const toastType = action.level === 'error'
          ? 'error'
          : action.level === 'info'
            ? 'info'
            : 'success';
        showToast(action.message, toastType);
      }

      if (action.type === 'integration.connect') {
        const provider = typeof action.provider === 'string' ? action.provider.toLowerCase() : '';
        if (provider === 'google' || provider === 'email' || provider === 'calendar' || provider === 'drive') {
          // Open the same modal that the manual "Connect Google" button opens
          window.dispatchEvent(new Event('openGoogleSyncModal'));
        }
        continue;
      }
    }
}

